/** API functions for import tracking and discrepancy management. */

import { get, patch, post } from './client.ts'
import type {
  ImportRun,
  ImportRunStatus,
  ImportDiscrepancy,
  ImportSummary,
  Resolution,
} from '../types/imports.ts'

export function getImportSummary(): Promise<ImportSummary> {
  return get<ImportSummary>('/imports/summary')
}

export function listImportRuns(params?: {
  skip?: number
  limit?: number
  status?: string
}): Promise<ImportRun[]> {
  const search = new URLSearchParams()
  if (params?.skip) search.set('skip', String(params.skip))
  if (params?.limit) search.set('limit', String(params.limit))
  if (params?.status) search.set('status', params.status)
  const qs = search.toString()
  return get<ImportRun[]>(`/imports/runs${qs ? `?${qs}` : ''}`)
}

export function getImportRun(id: number): Promise<ImportRunStatus> {
  return get<ImportRunStatus>(`/imports/runs/${id}`)
}

export function getLatestImportRun(): Promise<ImportRunStatus | null> {
  return get<ImportRunStatus | null>('/imports/runs/latest')
}

export function listDiscrepancies(params?: {
  skip?: number
  limit?: number
  run_id?: number
  resolution?: string
  discrepancy_type?: string
  table_name?: string
}): Promise<ImportDiscrepancy[]> {
  const search = new URLSearchParams()
  if (params?.skip) search.set('skip', String(params.skip))
  if (params?.limit) search.set('limit', String(params.limit))
  if (params?.run_id) search.set('run_id', String(params.run_id))
  if (params?.resolution) search.set('resolution', params.resolution)
  if (params?.discrepancy_type) search.set('discrepancy_type', params.discrepancy_type)
  if (params?.table_name) search.set('table_name', params.table_name)
  const qs = search.toString()
  return get<ImportDiscrepancy[]>(`/imports/discrepancies${qs ? `?${qs}` : ''}`)
}

export function getDiscrepancyCount(params?: {
  run_id?: number
  resolution?: string
  discrepancy_type?: string
  table_name?: string
}): Promise<{ count: number }> {
  const search = new URLSearchParams()
  if (params?.run_id) search.set('run_id', String(params.run_id))
  if (params?.resolution) search.set('resolution', params.resolution)
  if (params?.discrepancy_type) search.set('discrepancy_type', params.discrepancy_type)
  if (params?.table_name) search.set('table_name', params.table_name)
  const qs = search.toString()
  return get<{ count: number }>(`/imports/discrepancies/count${qs ? `?${qs}` : ''}`)
}

export function resolveDiscrepancy(
  id: number,
  resolution: Resolution,
): Promise<ImportDiscrepancy> {
  return patch<ImportDiscrepancy>(`/imports/discrepancies/${id}/resolve`, { resolution })
}

export function bulkResolveDiscrepancies(
  ids: number[],
  resolution: Resolution,
): Promise<{ resolved: number }> {
  return post<{ resolved: number }>('/imports/discrepancies/bulk-resolve', { ids, resolution })
}
